import h from 'hyperscript';
import Control from 'ol/control/Control';
import Attribution from 'ol/control/Attribution';
import ScaleLine from 'ol/control/ScaleLine';
import MousePosition from 'ol/control/MousePosition';
import Projection from 'ol/proj/Projection';
import View from 'ol/View';
import OverviewMap from 'ol/control/OverviewMap';
import { transform, transformExtent } from 'ol/proj';
import debounce from 'lodash-es/debounce';
import markerIcon from '@disy/cadenza-icons/map-marker.svg';
import overviewMapHideIcon from '@disy/cadenza-icons/overview-map-hide.svg';
import overviewMapShowIcon from '@disy/cadenza-icons/overview-map-show.svg';
import warningIcon from '@disy/cadenza-icons/warning.svg';

import { isExternal } from 'cadenza/utils/cadenza-url/cadenza-url';
import { deleteUrlParam, getUrlParam, hasParameter, setParam } from 'cadenza/utils/url-param-utils';
import { createLayer } from 'cadenza/map/layer-utils';
import { aspectRatioForMapExtent, calculateHeight, calculateWidth, getControl } from 'cadenza/map/map-utils';
import { formatCoordinate } from 'cadenza/utils/format/format';
import { createProxy } from 'cadenza/utils/proxy';
import { icon } from 'cadenza/utils/icon/icon';

import './map-controls.css';

import { currentWindowMediaXsOnly } from 'cadenza/utils/media';
import { CompactLegend } from 'cadenza/map/legend/compact-legend';
import { MapProperty } from 'cadenza/map/map-property';
import { logger } from 'cadenza/utils/logging';

import { getIntersection } from 'ol/extent';

import {
  BlockIdentifier,
  BlockVisibilityValues,
  getBlockSettingsSingleton
} from 'cadenza/map/block-settings';

import { createToggletip } from 'ui/toggletip/toggletip';

import i18n from './messages.properties';
import { ScaleInput } from './scale-input';

const MAP_MARKER_ICON = icon(markerIcon);
const OVERVIEW_MAP_MIN_HEIGHT = '30px';
const OVERVIEW_MAP_MIN_WIDTH = '30px';
const XS_ONLY_OVERVIEW_MAP_SIZE = 100;
const SM_UP_OVERVIEW_MAP_SIZE = 125;

export class MapControls extends Control {

  #element;
  _controls;
  #overviewMap;
  #compactLegend;
  #mousePosition;
  #customAttribution;
  #overlayToCollapseAfterResizeToXs;
  #overlayToReopenAfterResizeToSmUp;
  #projectionDescription;
  #scaleLine;
  #scaleInput;
  #blockSettings;

  constructor (config, { overviewMapContext = undefined, srs: { srsTitle, srsCode, usesDefaultSrs } = {}, overlayContainer = {} } = {}) {
    const element = h('.d-map-controls.dark-bg');
    super({ element });
    this.#element = element;
    const target = this.element;
    this._controls = [];
    this.#blockSettings = getBlockSettingsSingleton();

    const hasWorkbookMapControls = config.controls.workbookControls;
    const hasCompactLegend = this.#blockSettings.isBlockEnabled(BlockIdentifier.COMPACT_LEGEND, config.controls.compactLegend);

    if (hasCompactLegend) {
      const compactLegendOptions = hasWorkbookMapControls ? { overlayTarget: overlayContainer.element } : {};
      const isCollapsedByDefault = !this.#blockSettings.isBlockVisible(BlockIdentifier.COMPACT_LEGEND, !config.controls.compactLegend?.isMinimized);
      this.#compactLegend = new CompactLegend(target, { ...compactLegendOptions, isCollapsedByDefault });
      this._controls.push(this.#compactLegend);
    }

    this.#customAttribution = new CustomAttribution(config.map.attributions, { target, collapsible: false });
    this._controls.push(this.#customAttribution);

    if (this.#blockSettings.isBlockEnabled(BlockIdentifier.MOUSE_POSITION, config.controls.mousePosition)) {
      this.#mousePosition = new CustomMousePosition({ target, coordinateFormat: formatCoordinate });
      this._controls.push(this.#mousePosition);
    }

    if (this.#blockSettings.isBlockEnabled(BlockIdentifier.PROJECTION_DESCRIPTION, config.controls.projectionDescription)) {
      this.#projectionDescription = createCustomProjectionDescription({ target }, srsCode, srsTitle, usesDefaultSrs);
      this._controls.push(this.#projectionDescription);

      this.#projectionDescription.element.hidden = !this.#blockSettings.isBlockVisible(BlockIdentifier.PROJECTION_DESCRIPTION, true);
      this.#blockSettings.on(`change:${BlockIdentifier.PROJECTION_DESCRIPTION}`, () => {
        this.#projectionDescription.element.hidden = !this.#blockSettings.isBlockVisible(BlockIdentifier.PROJECTION_DESCRIPTION);
      });
    }

    const scaleInputEnabled = this.#blockSettings.isBlockEnabled(BlockIdentifier.SCALE_INPUT, config.controls.scaleInput);
    const scaleReadonlyEnabled = this.#blockSettings.isBlockEnabled(BlockIdentifier.SCALE_READONLY, config.controls.scaleReadonly);

    if (scaleInputEnabled || scaleReadonlyEnabled) {
      this.#scaleInput = new ScaleInput({ target, readonly: scaleReadonlyEnabled });

      this._controls.push(this.#scaleInput);
      this.#scaleInput.element.hidden = !this.#blockSettings.isBlockVisible(BlockIdentifier.SCALE_INPUT, true);
      this.#blockSettings.on(`change:${BlockIdentifier.SCALE_INPUT}`, () => {
        this.#scaleInput.element.hidden = !this.#blockSettings.isBlockVisible(BlockIdentifier.SCALE_INPUT);
      });
    }

    if (this.#blockSettings.isBlockEnabled(BlockIdentifier.SCALE_LINE, config.controls.scaleLine)) {
      this.#scaleLine = new CustomScaleLine({ target });
      this._controls.push(this.#scaleLine);

      this.#scaleLine.element.hidden = !this.#blockSettings.isBlockVisible(BlockIdentifier.SCALE_LINE, true);
      this.#blockSettings.on(`change:${BlockIdentifier.SCALE_LINE}`, () => {
        this.#scaleLine.element.hidden = !this.#blockSettings.isBlockVisible(BlockIdentifier.SCALE_LINE);
      });
    }

    if (overviewMapContext && this.#blockSettings.isBlockEnabled(BlockIdentifier.OVERVIEW_MAP, config.controls.overviewMap)) {
      if (overviewMapContext.currentExtent) {
        const isCollapsedByDefault = !this.#blockSettings.isBlockVisible(BlockIdentifier.OVERVIEW_MAP, !config.controls.overviewMap?.isMinimized);
        this.#overviewMap = createOverviewMap(target, { ...overviewMapContext, isCollapsedByDefault });
        this.#overviewMap.addOpenListener(() => this._onOpenOverlay(this.#overviewMap, this.#compactLegend));
        if (this.#compactLegend) {
          this.#compactLegend.addOpenListener(() => this._onOpenOverlay(this.#compactLegend, this.#overviewMap));
        }
        this._controls.push(this.#overviewMap);
        window.addEventListener('resize', () => this._onWindowResize());
      } else {
        logger.error('Overview map context is missing currentExtent attribute.');
      }
    }

    if (config.controls.lightBackground) {
      this.element.classList.remove('dark-bg');
      this.element.classList.add('is-light-background');
    }

    this._controls.forEach(c => c.element.classList.remove('ol-control')); // remove default styling
    this.element.onpointermove = event => event.stopPropagation(); // prevent map tip
    this.element.onpointerdown = event => event.stopPropagation(); // prevent clicks on the map being registered behind controls bar, e.g. opening object info
  }

  setMap (map) {
    super.setMap(map);
    this._applyLegendVisibility(map);
    this.#blockSettings.on(`change:${BlockIdentifier.MAP_LEGEND}`, () => this._applyLegendVisibility(map));

    this._controls.forEach(control => control.setMap(map));
    this.#element.insertBefore(h('.d-map-controls--spacer'), this.#customAttribution.element.nextSibling);

    if (this.#overviewMap) {
      this.#blockSettings.on(`change:${BlockIdentifier.OVERVIEW_MAP}`, () => {
        if (!this.#blockSettings.isBlockVisible(BlockIdentifier.OVERVIEW_MAP)) {
          this._onCloseOverlay(this.#overviewMap);
        }
      });
    }

    if (this.#compactLegend) {
      this.#blockSettings.on(`change:${BlockIdentifier.COMPACT_LEGEND}`, () => {
        if (!this.#blockSettings.isBlockVisible(BlockIdentifier.COMPACT_LEGEND)) {
          this._onCloseOverlay(this.#compactLegend);
        }
      });
    }
  }

  disposeInternal () {
    super.disposeInternal();
    this._controls.forEach(control => control.disposeInternal());
  }

  _applyLegendVisibility (map) {
    const isLegendVisible = this.#blockSettings.isBlockVisible(BlockIdentifier.MAP_LEGEND);
    this.#element.classList.toggle('d-map-controls--legend-visible', isLegendVisible);
  }

  _onOpenOverlay (openedOverlay, otherOverlay) {
    this.#overlayToCollapseAfterResizeToXs = otherOverlay;
    if (this._hasBothOverlaysOpen()) {
      if (currentWindowMediaXsOnly.matches) {
        otherOverlay.setCollapsedWithoutNotifications(true);
        this.#overlayToReopenAfterResizeToSmUp = otherOverlay;
      } else {
        this.#overlayToReopenAfterResizeToSmUp = openedOverlay;
      }
    } else {
      this.#overlayToReopenAfterResizeToSmUp = null;
    }
  }

  _onCloseOverlay (closedOverlay) {
    if (this.#overlayToReopenAfterResizeToSmUp === closedOverlay) {
      this.#overlayToReopenAfterResizeToSmUp = null;
    }
  }

  _hasBothOverlaysOpen () {
    return this._hasOverviewMapOpen() && this._hasCompactLegendOpen();
  }

  _hasOverviewMapOpen () {
    return !this.#overviewMap.getCollapsed();
  }

  _hasCompactLegendOpen () {
    return this.#compactLegend && !this.#compactLegend.getCollapsed();
  }

  _onWindowResize () {
    if (currentWindowMediaXsOnly.matches) {
      if (!this.#overviewMap.getCollapsed() && this._hasCompactLegendOpen()
        && this.#overlayToCollapseAfterResizeToXs) {
        this.#overlayToCollapseAfterResizeToXs.setCollapsedWithoutNotifications(true);
        this.#overlayToReopenAfterResizeToSmUp = this.#overlayToCollapseAfterResizeToXs;
      }
    } else if (this.#overlayToReopenAfterResizeToSmUp) {
      this.#overlayToReopenAfterResizeToSmUp.setCollapsedWithoutNotifications(false);
    }
  }

  getControl (controlClass) {
    return this._controls.find(control => control instanceof controlClass);
  }

  getControlsBar () {
    return this.#element;
  }

}

export function getInnerControl (map, controlClass) {
  const mapControls = getControl(map, MapControls);
  return mapControls.getControl(controlClass);
}

function createOverviewMap (target, { layers, projection: { code, units }, currentExtent, maxExtent, locatorScaleMode, hidpi, isCollapsedByDefault }) {
  const overviewMap = new CustomOverviewMap({
    target,
    view: new View({
      projection: new Projection({ code, units })
    }),
    // If currentExtent is bigger than maxExtent (does not make sense but can occur in some customer repositories)
    // the displayed extent is limited to the maxExtent (same behaviour as in Desktop).
    extent: getIntersection(currentExtent, maxExtent),
    maxExtent,
    locatorScaleMode,
    isCollapsedByDefault
  });
  const overviewMapElement = overviewMap.getOverviewMap();
  if (layers) {
    layers.forEach(layerConfig => {
      if (!isExternal(layerConfig.source.url)) {
        if (!layerConfig.source.params) {
          layerConfig.source.params = {};
        }
        layerConfig.source.params.locator = true;
      }
      overviewMapElement.set(MapProperty.HIDPI, hidpi);
      overviewMapElement.addLayer(createLayer(layerConfig, overviewMapElement));
    });
  }
  return overviewMap;
}

// Overview map with customized UI and support for different projections of map and overview map (OL does not support it, at least not in version 5.3.0)
class CustomOverviewMap extends OverviewMap {

  #openListeners = [];
  #suppressNextOpenClosedNotification;
  #aspectRatio;
  #setOverviewMapAspectRatioDebounced;
  #blockSettings;

  constructor ({ target, layers, view, extent, maxExtent, locatorScaleMode, isCollapsedByDefault = false } = {}) {
    super({
      collapseLabel: icon(overviewMapHideIcon),
      label: icon(overviewMapShowIcon),
      layers,
      target,
      tipLabel: i18n('overviewmap.title'),
      view
    });
    this._isCollapsedByDefault = isCollapsedByDefault;
    this.element.querySelector('button').classList.add('button', 'button-s', 'button-icon', 'button-invert');
    this.updateCollapsed_ = this.updateCollapsed_.bind(this);
    this._extent = extent; // initial value
    this._maxExtent = maxExtent;
    this._mapLocatorScaleMode = locatorScaleMode;
    this.#aspectRatio = aspectRatioForMapExtent(extent);
    this.#setOverviewMapAspectRatioDebounced = debounce((mapWithAspectRatio) => {
      this._updateOverviewMapDynamically(mapWithAspectRatio);
    }, 300);
    this.#blockSettings = getBlockSettingsSingleton();
    this.#blockSettings.on(`change:${BlockIdentifier.OVERVIEW_MAP}`, () => {
      this.setCollapsed(!this.#blockSettings.isBlockVisible(BlockIdentifier.OVERVIEW_MAP));
    });
  }

  initDynamicExtent (map) {
    // ensure that the dynamic extent is set.
    let dynamicExtent = map.get(MapProperty.OVERVIEW_MAP_DYNAMIC_EXTENT);
    if (dynamicExtent === null || dynamicExtent === undefined) {
      dynamicExtent = hasParameter(MapProperty.OVERVIEW_MAP_DYNAMIC_EXTENT)
        ? getUrlParam({ name: MapProperty.OVERVIEW_MAP_DYNAMIC_EXTENT }) === 'true'
        : this._mapLocatorScaleMode === 'DYNAMIC';
      map.set(
        MapProperty.OVERVIEW_MAP_DYNAMIC_EXTENT,
        dynamicExtent);
    }
  }

  initDynamicAspectRatio (map) {
    map.set(MapProperty.OVERVIEW_MAP_DYNAMIC_ASPECT_RATIO, true);
  }

  setMap (map) {
    const mapProjection = map.getView().getProjection();
    const overviewMapProjection = this.ovmap_.getView().getProjection();
    let mapProxy = map;
    if (mapProjection.getCode() !== overviewMapProjection.getCode()) {
      mapProxy = createReprojectedMapProxy(map, mapProjection, overviewMapProjection);
    }
    this.initDynamicExtent(map);
    this.initDynamicAspectRatio(map);
    super.setMap(mapProxy);
    this._updateOverviewMapDynamically(map.get(MapProperty.OVERVIEW_MAP_DYNAMIC_ASPECT_RATIO));
    // If there is a view center, the view is already initialized.
    if (map.getView().getCenter()) {
      this.onInitView_();
    } else {
      map.once('change:view', () => this.onInitView_());
    }

    map.on(`change:${MapProperty.OVERVIEW_MAP_DYNAMIC_ASPECT_RATIO}`,
      () => {
        this._updateOverviewMapDynamically(map.get(MapProperty.OVERVIEW_MAP_DYNAMIC_ASPECT_RATIO));
      });
    map.on(`change:${MapProperty.SIZE}`, () => {
      this.#setOverviewMapAspectRatioDebounced(map.get(MapProperty.OVERVIEW_MAP_DYNAMIC_ASPECT_RATIO));
    });
  }

  addOpenListener (listener) {
    this.#openListeners.push(listener);
  }

  setCollapsedWithoutNotifications (collapsed) {
    this.#suppressNextOpenClosedNotification = true;
    return super.setCollapsed(collapsed);
  }

  validateExtent_ () {
    // Do not reset the extent or recenter, we don't want the overview map to move
  }

  onInitView_ () {
    // We set the collapsed state here, because if we pass it
    // as constructor option, the overview map is initially blank.
    this.initCollapsed_();
    // initZoom_() has to be called after initCollapsed_() because setCollapsed calls resetExtent_()
    // if the overview map has not been rendered yet, which sets the zoom level depending on the main
    // map zoom level.
    this.initZoom_();
  }

  initZoom_ () {
    const overviewView = this.getOverviewMap().getView();
    overviewView.fit(this._extent);
    const resolution = overviewView.getResolution();
    const zoom = overviewView.getZoomForResolution(resolution);
    const center = overviewView.getCenter();
    this.getMap().on(`change:${MapProperty.OVERVIEW_MAP_DYNAMIC_EXTENT}`, () => {
      if (!this.getMap().get(MapProperty.OVERVIEW_MAP_DYNAMIC_EXTENT)) {
        deleteUrlParam(MapProperty.OVERVIEW_MAP_DYNAMIC_EXTENT);
        overviewView.setMinZoom(zoom);
        overviewView.setMaxZoom(zoom);
        overviewView.setCenter(center);
        overviewView.setResolution(resolution);
        this.render();
      } else {
        setParam({ paramName: MapProperty.OVERVIEW_MAP_DYNAMIC_EXTENT, paramValue: true });
        this._updateDynamicExtent();
      }
    });
    // FIXME (babenhauserheide): this is a hack to get control, because I did not find a way to revert to the vanilla behavior. It is slow, but it works.
    this.getMap().getView().addEventListener('change', () => {
      this._updateDynamicExtent();
    });
    // trigger the update once to init the extent
    this.getOverviewMap().once('postrender', () => {
      const overviewMapDomElement = this.getOverviewMap().getTargetElement();
      overviewMapDomElement.dataset.testid = 'map-overview-map';
      this._updateDynamicExtent();
    });
  }

  _updateDynamicExtent () {
    const overviewView = this.getOverviewMap().getView();
    overviewView.beginInteraction();
    if (this.getMap().get(MapProperty.OVERVIEW_MAP_DYNAMIC_EXTENT)) {
      overviewView.setMinZoom(0);
      overviewView.setMaxZoom(99); // arbitrary high value
      const mapView = this.getMap().getView();
      // TODO_LATER (babenhauserheide): replace with single step calculation of fixed ratio between map rectangle and overview map
      // ensure that the box is smaller than the overview, then scale up to factor 2
      const ARBITRARY_SCALING = 50;
      overviewView.setResolution(mapView.getResolution() * ARBITRARY_SCALING);
      // render to update the box
      this.render(); // do not pass MapEvent, because it is not required in OverviewMap.render()
      const boxWidth = this.element.querySelector('.ol-overviewmap-box').offsetWidth;
      const overviewWidth = this.getOverviewMap().getTargetElement().clientWidth;
      const boxHeight = this.element.querySelector('.ol-overviewmap-box').offsetHeight;
      const overviewHeight = this.getOverviewMap().getTargetElement().clientHeight;
      const boxDivider = Math.min(overviewWidth / boxWidth, overviewHeight / boxHeight);
      const resolutionToFitBoxInOverviewMap = mapView.getResolution() * ARBITRARY_SCALING / boxDivider;
      // match to Desktop: box width should be 1/4 of overview width
      let targetResolution = 4 * resolutionToFitBoxInOverviewMap;
      // adhere to max extent as defined for the overview map
      if (this._maxExtent) {
        const overviewMapMaxResolution = overviewView.getResolutionForExtent(this._maxExtent);
        targetResolution = Math.min(overviewMapMaxResolution, targetResolution);
      }
      overviewView.setResolution(targetResolution);
      overviewView.setCenter(mapView.getCenterInternal());
    }
    // update box
    this.render(); // do not pass MapEvent, because it is not required in OverviewMap.render()
    overviewView.endInteraction();
  }

  initCollapsed_ () {
    this.isAutoChange_ = true;
    const collapsed = this._getInitialCollapsedValue();
    this.setCollapsed(collapsed);
    if (collapsed) {
      this.updateCollapsed_();
      currentWindowMediaXsOnly.addListener(this.updateCollapsed_);
    }
    this.isAutoChange_ = false;
  }

  updateCollapsed_ () {
    this.isAutoChange_ = true;
    this.setCollapsed(currentWindowMediaXsOnly.matches || this._isCollapsedByDefault);
    this.isAutoChange_ = false;
  }

  handleToggle_ () {
    super.handleToggle_();
    const collapsed = this.getCollapsed();
    this._getMapElement().classList.toggle('with-overviewmap', !collapsed);

    if (!this.#suppressNextOpenClosedNotification) {
      if (!this.isAutoChange_) {
        currentWindowMediaXsOnly.removeListener(this.updateCollapsed_);
      }

      if (!collapsed) {
        for (const listener of this.#openListeners) {
          listener();
        }
        // if current resolution of the overview map is higher than maxExtent then calculate
        // then override current resolution to fit in maxExtent (otherwise the overview map can be blank)
        if (this._maxExtent) {
          const overviewView = this.getOverviewMap().getView();
          const overviewMapMaxResolution = overviewView.getResolutionForExtent(this._maxExtent);
          let resolution = overviewView.getResolution();
          if (resolution > overviewMapMaxResolution) {
            resolution = Math.min(overviewMapMaxResolution, resolution);
            overviewView.setResolution(resolution);
          }
        }
      }
    }
    if (!this.isAutoChange_) {
      this.#blockSettings.setVisibility(BlockIdentifier.OVERVIEW_MAP, collapsed ? BlockVisibilityValues.minimized : BlockVisibilityValues.regular);
    }
    this.#suppressNextOpenClosedNotification = false;
  }

  _getInitialCollapsedValue () {
    const persistedState = this.#blockSettings.isBlockVisible(BlockIdentifier.OVERVIEW_MAP);
    if (persistedState) {
      return persistedState === 'true';
    }
    return this._isCollapsedByDefault;
  }

  _getMapElement () {
    return this.getMap().getViewport().closest('.map');
  }

  _constrainOverviewMapSize () {
    this.getOverviewMap().getTarget().style.maxWidth = `${this._getMapElement().offsetWidth / 3}px`;
    this.getOverviewMap().getTarget().style.maxHeight = `${this._getMapElement().offsetHeight / 3}px`;
    this.getOverviewMap().getTarget().style.minWidth = OVERVIEW_MAP_MIN_WIDTH;
    this.getOverviewMap().getTarget().style.minHeight = OVERVIEW_MAP_MIN_HEIGHT;
  }

  _setAspectRatio () {
    const smallerSideSize = this._smallerSideSize();
    if (this.#aspectRatio < 1) {
      // overview maps with portrait aspect ratio get a fixed width and a height calculated from the aspect ratio
      const fixedWidth = smallerSideSize;
      const calculatedHeight = calculateHeight(fixedWidth, this.#aspectRatio);
      this.getOverviewMap().getTarget().style.width = `${fixedWidth}px`;
      this.getOverviewMap().getTarget().style.height = `${calculatedHeight}px`;
      this.getOverviewMap().getTarget().style.top = `${-(calculatedHeight + 10)}px`;
    } else {
      // overview maps with landscape aspect ratio get a fixed height and a width calculated from the aspect ratio
      const fixedHeight = smallerSideSize;
      this.getOverviewMap().getTarget().style.height = `${fixedHeight}px`;
      this.getOverviewMap().getTarget().style.width = `${calculateWidth(fixedHeight, this.#aspectRatio)}px`;
      this.getOverviewMap().getTarget().style.top = `${-(fixedHeight + 10)}px`;
    }
  }

  _resetSquare () {
    const smallerSideSize = this._smallerSideSize();
    this.getOverviewMap().getTarget().style.width = `${smallerSideSize}px`;
    this.getOverviewMap().getTarget().style.height = `${smallerSideSize}px`;
    this.getOverviewMap().getTarget().style.top = `-${smallerSideSize + 10}px`;
  }

  _smallerSideSize () {
    return currentWindowMediaXsOnly.matches ? XS_ONLY_OVERVIEW_MAP_SIZE : SM_UP_OVERVIEW_MAP_SIZE;
  }

  _updateTopPosition () {
    const overviewMapMaxHeight = parseInt(this.getOverviewMap().getTarget().style.maxHeight, 10);
    const overviewMapHeight = parseInt(this.getOverviewMap().getTarget().style.height, 10);
    if (overviewMapMaxHeight < overviewMapHeight) {
      this.getOverviewMap().getTarget().style.top = `${-(overviewMapMaxHeight + 10)}px`;
    }
  }

  _updateOverviewMapDynamically (mapWithAspectRatio) {
    this._constrainOverviewMapSize();
    if (mapWithAspectRatio) {
      this._setAspectRatio();
    } else {
      this._resetSquare();
    }
    this._updateTopPosition();
    this.getOverviewMap().updateSize();
    this.getOverviewMap().getView().fit(this._extent);
  }

}

function createReprojectedMapProxy (map, mapProjection, overviewMapProjection) {
  const view = map.getView();
  view.updateAnimationKey_ = false; // hack for Proxy polyfill for IE11 - this polyfill only allows known properties to be proxied, no more can be added after creation.

  const viewProxy = createProxy(view, {

    calculateExtentInternal: mapSize => {
      const extent = view.calculateExtentInternal(mapSize);
      return transformExtent(extent, mapProjection, overviewMapProjection);
    },

    getCenterInternal: () => {
      const center = view.getCenterInternal();
      return transform(center, mapProjection, overviewMapProjection);
    },

    setCenterInternal: center => {
      const centerInMapProjection = transform(center, overviewMapProjection, mapProjection);
      view.setCenterInternal(centerInMapProjection);
    }
  });

  return createProxy(map, {
    getView: () => viewProxy
  });
}

class CustomScaleLine extends ScaleLine {

  constructor (options) {
    super(options);
    this.labelElement_ = h('span.ol-scale-line-label');
    this.element.prepend(this.labelElement_);
  }

  updateElement_ () {
    const previousHtml = this.renderedHTML_;
    super.updateElement_();
    if (previousHtml !== this.renderedHTML_) {
      /* eslint-disable no-unsanitized/property */
      this.innerElement_.innerHTML = '';
      this.labelElement_.innerHTML = this.renderedHTML_;
    }
  }

}

function createCustomProjectionDescription ({ target }, code, title, usesDefaultSrs) {
  const element = document.createElement('div');
  if (usesDefaultSrs) {
    const defaultSrsWarningButton = createToggletip({
      toggletip: i18n('map.default-projection-warning'),
      icon: icon(warningIcon),
      styleClass: 'default-projection-warning'
    });
    defaultSrsWarningButton.classList.add('button-warning');
    element.appendChild(defaultSrsWarningButton);
  }
  element.className = 'ol-control-projection-desc ol-unselectable ol-control';
  const label = h('span.ol-projection-desc--label.ellipsis', `${title || ''} ${code || ''}`);
  element.appendChild(label);
  return new Control({
    target,
    element
  });
}

class CustomAttribution extends Attribution {

  constructor (mapAttributions = [], options) {
    super(options);
    this.mapAttributtions = mapAttributions;
  }

  getSourceAttributions_ (frameState) {
    return this.mapAttributtions.concat(super.getSourceAttributions_(frameState));
  }

}

class CustomMousePosition extends MousePosition {

  updateHTML_ (pixel) {
    super.updateHTML_(pixel);
    if (this.element.innerHTML !== '&nbsp;') {
      this.element.prepend(MAP_MARKER_ICON);
    }
  }

}
